/**
 * Booking several sessions of one service up front. Mirrors Services/ProSessionPricing.cs for
 * display and the offer's opening number; the server reads the deal from its own table.
 */
export const MAX_SESSIONS_CAP = 12;

/** The best deal this many sessions has reached; 0 below the first one. */
export const percentFor = (discounts, sessions) => (discounts || [])
  .filter((d) => d.sessions <= sessions)
  .reduce((best, d) => Math.max(best, d.percentOff), 0);

export const totalCents = (priceCents, sessions, percentOff) => Math.round((priceCents * sessions * (100 - percentOff)) / 100);

/** The best deal the service offers at all, for the line on the page. */
export const bestPercent = (discounts) => (discounts || []).reduce((best, d) => Math.max(best, d.percentOff), 0);

/**
 * The start of each session, `everyDays` apart. Steps the local calendar date, so a session stays
 * at the same wall-clock time across a daylight-saving change.
 */
export function repeatStarts(firstIso, count, everyDays) {
  const first = new Date(firstIso);
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(first);
    d.setDate(d.getDate() + i * everyDays);
    return d.toISOString();
  });
}

/** Weekday chips, Monday first, keyed by `Date#getDay()`. */
export const WEEKDAYS = [[1, 'MON'], [2, 'TUE'], [3, 'WED'], [4, 'THU'], [5, 'FRI'], [6, 'SAT'], [0, 'SUN']];

/**
 * CUSTOM DAYS: the first session as picked, then the next matching weekdays after it (FRI SAT SUN
 * fills Fri, Sat, Sun, the next Fri…) at the same wall-clock time, until there are `count`. The
 * first session stays where it was put even when its own day isn't one of them. No day picked
 * gives the first session alone, so the caller can say what is missing.
 */
export function repeatOnWeekdays(firstIso, count, weekdays) {
  const days = new Set((weekdays || []).map(Number).filter((d) => d >= 0 && d <= 6));
  const first = new Date(firstIso);
  const out = [first.toISOString()];
  if (days.size === 0) return out;
  for (let i = 1; out.length < count && i <= 7 * count; i += 1) {
    const d = new Date(first);
    d.setDate(d.getDate() + i);
    if (days.has(d.getDay())) out.push(d.toISOString());
  }
  return out;
}

const pad = (n) => String(n).padStart(2, '0');
/** A date input's value for an instant, in the reader's zone. */
export const localDateValue = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };

/** The same instant's wall-clock time moved onto another local date. */
export function moveToDate(iso, dateValue) {
  const [y, m, day] = String(dateValue).split('-').map(Number);
  if (!y || !m || !day) return iso;
  const d = new Date(iso);
  d.setFullYear(y, m - 1, day);
  return d.toISOString();
}

/** A time input's value (HH:MM) for an instant, in the reader's zone. */
export const localTimeValue = (iso) => { const d = new Date(iso); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };

/** The same local date at another wall-clock start time. The length stays the service's. */
export function moveToTime(iso, timeValue) {
  const [hh, mm] = String(timeValue).split(':').map(Number);
  if (!Number.isInteger(hh) || !Number.isInteger(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return iso;
  const d = new Date(iso);
  d.setHours(hh, mm, 0, 0);
  return d.toISOString();
}
